import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'motion/react';
import { doc, setDoc, addDoc, deleteDoc, collection, query, getDocs } from 'firebase/firestore';
import { auth, db, handleFirestoreError, OperationType } from '../firebase';
import { cn, calculateOVR, blendScoutingOVR, currentSeasonGames } from '../lib/utils';
import { calculateXOVR } from '../lib/xovr-engine';
import { computePlayerHotCold } from '../lib/streaks';
import { tapLight } from '../lib/haptics';
import { buildFieldTimeReport } from '../lib/field-time-report';
import { XOVRBreakdown as XOVRBreakdownCard } from './XOVRBreakdown';
import { XOVRTrendChart } from './XOVRTrendChart';
import { FieldTimeReportModal } from './FieldTimeReport/FieldTimeReportModal';
import { Plus, Share2, FileText, Upload, ChevronRight } from 'lucide-react';

export const PlayerForm = ({
  teamId,
  player,
  team,
  defaultIsGuest = false,
  includeAssistantScouting = false,
  onGoToImport,
  initialTab,
  onTabChange,
  hasPrevPlayer = false,
  hasNextPlayer = false,
  onPrevPlayer,
  onNextPlayer,
  onClose
}: {
  teamId: string;
  player?: any;
  team?: any;
  defaultIsGuest?: boolean;
  includeAssistantScouting?: boolean;
  onGoToImport?: () => void;
  initialTab?: 'stats' | 'attributes' | 'positions';
  onTabChange?: (tab: 'stats' | 'attributes' | 'positions') => void;
  hasPrevPlayer?: boolean;
  hasNextPlayer?: boolean;
  onPrevPlayer?: () => void;
  onNextPlayer?: () => void;
  onClose: () => void
}) => {
  const [formData, setFormData] = useState(player || {
    name: '',
    number: '',
    isGuest: defaultIsGuest,
    guestActive: true,
    attributes: {
      hitting: { contact: 3, power: 3, eye: 3, approach: 3 },
      fielding: { glove: 3, arm: 3, block: 3 },
      intangibles: { speed: 3, iq: 3, clutch: 3 },
      pitching: { velocity: 0, control: 0 }
    },
    positions: {
      P: 0, C: 0, '1B': 0, '2B': 0, '3B': 0, SS: 0, LF: 0, CF: 0, RF: 0
    },
    primaryPosition: '',
    secondaryPosition: ''
  });

  const [activeTab, setActiveTab] = useState<'stats' | 'attributes' | 'positions'>(
    player ? (initialTab ?? 'stats') : 'attributes'
  );
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [gamesForStats, setGamesForStats] = useState<any[]>([]);
  const [isPitcher, setIsPitcher] = useState<boolean>(() => {
    const velo = player?.attributes?.pitching?.velocity ?? 0;
    const ctrl = player?.attributes?.pitching?.control ?? 0;
    return velo > 0 || ctrl > 0;
  });

  // Scoped to the team's current season at the fetch boundary, so everything
  // downstream (aggregated stats, the xOVR trendline, hot/cold, the field time
  // report) reflects THIS season only. Prior seasons live in Season History.
  useEffect(() => {
    if (!teamId) return;
    const fetchGames = async () => {
      const q = query(collection(db, `teams/${teamId}/games`));
      const snap = await getDocs(q);
      // Exclude the onboarding CSV's synthesized stats-container doc: it holds
      // a whole season's totals in one doc, which would count as a single
      // "game played" and wreck per-game aggregation, hot/cold streaks, and
      // the xOVR trend chart below (all of which assume one doc = one game).
      const all: any[] = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter((g: any) => g.source !== 'onboardingImport');
      setGamesForStats(currentSeasonGames(all, team));
    };
    fetchGames();
  }, [teamId, team?.season, team?.seasonYear]);

  // Aggregate stats for this specific player
  const aggregatedStats = (() => {
    if (!player) return null;
    const res = {
      gp: 0, pa: 0, ab: 0, h: 0, bb: 0, so: 0, rbi: 0, r: 0, avg: 0, ops: 0,
      p_gp: 0, ip: 0, sPct: 0, fpsPct: 0, p_bf: 0, era: 0, whip: 0,
      p_k: 0, p_bb: 0, p_h: 0, p_er: 0, totalPitches: 0,
    };
    
    let total_ab = 0, total_pa = 0, sum_avg = 0, sum_ops = 0;
    let total_pitches = 0, total_bf = 0, total_ip = 0, sum_era = 0, sum_whip = 0, sum_sPct = 0, sum_fpsPct = 0, sum_p_bf = 0;

    const normalizeRate = (val: any) => {
      if (val == null || val === '') return null;
      const n = parseFloat(val);
      if (isNaN(n)) return null;
      return n > 1 ? n / 100 : n;
    };

    gamesForStats.forEach(g => {
      const s = g.stats?.[player.id];
      if (!s) return;

      if (s.pa > 0 || s.ab > 0) {
        res.gp++;
        res.pa += (s.pa || 0);
        res.ab += (s.ab || 0);
        res.h += (s.h || 0);
        res.bb += (s.bb || 0);
        res.so += (s.so || 0);
        res.rbi += (s.rbi || 0);
        res.r += (s.r || 0);
        
        if (s.avg != null) {
          sum_avg += (s.avg * (s.ab || 1));
          total_ab += (s.ab || 1);
        }
        if (s.ops != null) {
          sum_ops += (s.ops * (s.pa || 1));
          total_pa += (s.pa || 1);
        }
      }

      if (s.ip > 0 || s.pitches > 0) {
        res.p_gp++;
        res.ip += (s.ip || 0);
        res.p_k  += (s.soAllowed || 0);
        res.p_bb += (s.bbAllowed || 0);
        res.p_h  += (s.hAllowed  || 0);
        res.p_er += (s.erAllowed || 0);
        res.totalPitches += (s.pitches || 0);
        total_ip += (s.ip || 0);
        total_pitches += (s.pitches || 0);
        const bf = parseInt(s.bf) || Math.round((s.ip || 0) * 4) || 1;
        total_bf += bf;

        if (s.era != null) sum_era += (s.era * (s.ip || 1));
        if (s.whip != null) sum_whip += (s.whip * (s.ip || 1));
        
        const gameSPct = normalizeRate(s.sPct);
        const gameFPSPct = normalizeRate(s.fpsPct);

        if (gameSPct != null) sum_sPct += (gameSPct * (s.pitches || 1));
        if (gameFPSPct != null) sum_fpsPct += (gameFPSPct * bf);
        if (s.p_bf != null) sum_p_bf += (parseFloat(s.p_bf) * bf);
      }
    });

    if (total_ab > 0) res.avg = sum_avg / total_ab;
    else if (res.ab > 0) res.avg = res.h / res.ab;
    if (total_pa > 0) res.ops = sum_ops / total_pa;
    else if (res.pa > 0 && res.ab > 0) {
      const obp = (res.h + res.bb) / res.pa;
      const slg = res.h / res.ab; // approximates singles-only; close enough without XBH data
      res.ops = obp + slg;
    }
    if (total_ip > 0) {
      res.era = sum_era / total_ip;
      res.whip = sum_whip / total_ip;
    }
    if (total_pitches > 0) res.sPct = sum_sPct / total_pitches;
    if (total_bf > 0) {
      res.fpsPct = sum_fpsPct / total_bf;
      res.p_bf = sum_p_bf / total_bf;
    }

    return res;
  })();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const attributesToSave = isPitcher
      ? formData.attributes
      : { ...formData.attributes, pitching: { velocity: 0, control: 0 } };

    // Recompute scouting OVR from updated attributes, blending assistant ratings if enabled
    const headCoachOVR = calculateOVR(attributesToSave, formData.primaryPosition);
    const scoutingOVR = blendScoutingOVR(headCoachOVR, formData.assistantRatings, includeAssistantScouting);

    // If the player already has season stats, recalculate xOVR anchored to the new
    // scoutingOVR so the breakdown never drifts from the current attribute ratings.
    // priorSeasonStats acts as a fallback when there's no current sample yet.
    // An imported GameChanger season snapshot is the authoritative current-season
    // sample when present (it's what the Stats tab shows), so xOVR uses it first.
    const existingStats = formData.seasonSnapshot?.stats ?? formData.stats ?? null;
    const priorStats = formData.priorSeasonStats?.stats ?? null;
    const breakdown = (existingStats || priorStats)
      ? calculateXOVR(scoutingOVR, existingStats, priorStats)
      : null;

    const finalData = {
      ...formData,
      attributes:  attributesToSave,
      ovr:         breakdown ? breakdown.xovr : scoutingOVR,
      ovrBreakdown: breakdown ?? null,
      teamId,
      coachId: auth.currentUser?.uid ?? '',
    };
    
    try {
      if (player?.id) {
        await setDoc(doc(db, `teams/${teamId}/players`, player.id), finalData);
      } else {
        await addDoc(collection(db, `teams/${teamId}/players`), finalData);
      }
      onClose();
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `teams/${teamId}/players`);
    }
  };

  const updateAttribute = (category: string, key: string, val: number) => {
    tapLight();
    setFormData({
      ...formData,
      attributes: {
        ...formData.attributes,
        [category]: {
          ...formData.attributes[category],
          [key]: val
        }
      }
    });
  };

  const updatePosition = (pos: string, val: number) => {
    tapLight();
    setFormData({
      ...formData,
      positions: {
        ...formData.positions,
        [pos]: val
      }
    });
  };

  // Compute hot/cold for this player from season games
  const hotCold = (() => {
    if (!player || gamesForStats.length < 2 || !aggregatedStats) return null;
    const statsMap = new Map([[player.id, aggregatedStats]]);
    return computePlayerHotCold(gamesForStats, statsMap).get(player.id) ?? null;
  })();

  const tabs = [
    { id: 'stats' as const,      label: 'Stats' },
    { id: 'attributes' as const, label: 'Attributes' },
    { id: 'positions' as const,  label: 'Positions' },
  ];

  // Field Time Report (fairness printout for parents) — existing players only.
  const [reportOpen, setReportOpen] = useState(false);
  const fieldTimeReport = useMemo(
    () => (player?.id && team ? buildFieldTimeReport(player, team, gamesForStats) : null),
    [player, team, gamesForStats]
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="fixed inset-0 z-[300] bg-surface flex flex-col safe-top"
    >
      {/* Sticky header */}
      <div className="flex-none px-6 pt-6 pb-3 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-black italic tracking-tight uppercase">
            {player ? `#${player.number} ${player.name}` : 'New Player'}
          </h2>
          <div className="flex items-center gap-1">
            {fieldTimeReport && (
              <button
                onClick={() => { tapLight(); setReportOpen(true); }}
                className="p-2 text-ink-2 hover:text-ink"
                title="Field Time Report"
                aria-label="Field Time Report"
              >
                <Share2 className="w-5 h-5" />
              </button>
            )}
            {/* Icon-only, so it needs an accessible name: a screen reader
                otherwise announces just "button". It also makes the close
                target addressable by name, rather than by DOM position behind
                the report button that sits before it. */}
            <button onClick={onClose} aria-label="Close" className="p-2 text-ink-2 hover:text-ink">
              <Plus className="w-6 h-6 rotate-45" />
            </button>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex gap-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); onTabChange?.(tab.id); }}
              className={cn(
                "px-4 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest transition-all border",
                activeTab === tab.id
                  ? "bg-canvas text-accent-ink border-ink-mute"
                  : "bg-canvas text-ink-2 border-hairline"
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Scrollable tab content — swipe left/right to move to the next/prev
          player (like a photo gallery). Only existing players get the gesture;
          the swipe remounts PlayerForm via key in the parent, re-seeding state. */}
      <motion.div
        className="flex-1 overflow-y-auto px-6 pb-12"
        drag={player && (hasPrevPlayer || hasNextPlayer) ? 'x' : false}
        dragDirectionLock
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.25}
        onDragEnd={(_, info) => {
          const SWIPE = 70; // px of horizontal travel before it counts as a swipe
          const horizontal = Math.abs(info.offset.x) > Math.abs(info.offset.y);
          if (!horizontal || Math.abs(info.offset.x) < SWIPE) return;
          if (info.offset.x < 0 && hasNextPlayer) { tapLight(); onNextPlayer?.(); }
          else if (info.offset.x > 0 && hasPrevPlayer) { tapLight(); onPrevPlayer?.(); }
        }}
      >
        <div className="max-w-md mx-auto space-y-6 pt-2">

          {/* ── Stats tab ── */}
          {activeTab === 'stats' && (
            <div className="space-y-4">

              {/* Streaks */}
              {hotCold && (hotCold.batting !== null || hotCold.pitching !== null) && (
                <div className="space-y-2">
                  {hotCold.topSignals.map((signal, i) => {
                    const isHot = hotCold.batting === 'hot' || hotCold.pitching === 'hot';
                    return (
                      <div
                        key={i}
                        className={cn(
                          "flex items-center gap-2 px-4 py-3 rounded-2xl border font-black",
                          isHot
                            ? "bg-gold-soft border-hairline text-gold-ink"
                            : "bg-canvas border-hairline text-ink"
                        )}
                      >
                        <span>{isHot ? '🔥' : '🧊'}</span>
                        <span className="uppercase tracking-wide text-[11px]">{signal}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Season stats */}
              {aggregatedStats && aggregatedStats.pa > 0 && (
                <div className="bg-canvas rounded-3xl border border-hairline p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Season Hitting</p>
                    <span className="text-[9px] font-black text-accent-ink uppercase px-2 py-0.5 bg-canvas rounded-full">
                      {aggregatedStats.gp} Games
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    {[
                      { label: 'AVG', value: aggregatedStats.avg > 0 ? aggregatedStats.avg.toFixed(3).replace(/^0/, '') : '.000', highlight: true },
                      { label: 'OPS', value: aggregatedStats.ops > 0 ? aggregatedStats.ops.toFixed(3).replace(/^0/, '') : '.000', highlight: true },
                      { label: 'PA',  value: aggregatedStats.pa },
                      { label: 'H',   value: aggregatedStats.h },
                      { label: 'RBI', value: aggregatedStats.rbi },
                      { label: 'BB',  value: aggregatedStats.bb },
                      { label: 'SO',  value: aggregatedStats.so },
                      { label: 'R',   value: aggregatedStats.r },
                    ].map(stat => (
                      <div key={stat.label} className="space-y-0.5">
                        <div className={cn("text-lg font-black tracking-tight", stat.highlight ? "text-ink" : "text-ink-2")}>
                          {stat.value}
                        </div>
                        <div className="text-[8px] font-semibold text-ink-2 uppercase tracking-widest">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {aggregatedStats && aggregatedStats.p_gp > 0 && (
                <div className="bg-canvas rounded-3xl border border-hairline p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Pitching Intel</p>
                    <span className="text-[9px] font-black text-ink uppercase px-2 py-0.5 bg-canvas rounded-full">
                      {aggregatedStats.p_gp} Outings
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    {[
                      { label: 'S%',   value: `${Math.round(aggregatedStats.sPct * 100)}%`,   color: aggregatedStats.sPct >= 0.6 ? 'text-accent-ink' : aggregatedStats.sPct < 0.5 ? 'text-danger' : 'text-ink' },
                      { label: 'FPS%', value: `${Math.round(aggregatedStats.fpsPct * 100)}%`, color: aggregatedStats.fpsPct >= 0.6 ? 'text-accent-ink' : aggregatedStats.fpsPct < 0.4 ? 'text-danger' : 'text-ink' },
                      { label: 'P/BF', value: aggregatedStats.p_bf.toFixed(1),                color: aggregatedStats.p_bf <= 3.8 ? 'text-accent-ink' : aggregatedStats.p_bf > 4.5 ? 'text-danger' : 'text-ink' },
                      { label: 'IP',   value: aggregatedStats.ip.toFixed(1) },
                      { label: 'ERA',  value: aggregatedStats.era.toFixed(2) },
                      { label: 'WHIP', value: aggregatedStats.whip.toFixed(2) },
                    ].map(stat => (
                      <div key={stat.label} className="space-y-0.5">
                        <div className={cn("text-lg font-black tracking-tight", stat.color || "text-ink-2")}>
                          {stat.value}
                        </div>
                        <div className="text-[8px] font-semibold text-ink-2 uppercase tracking-widest">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-hairline pt-3 grid grid-cols-5 gap-3">
                    {[
                      { label: 'K',  value: aggregatedStats.p_k,  color: aggregatedStats.p_k > aggregatedStats.p_bb ? 'text-accent-ink' : 'text-ink-2' },
                      { label: 'BB', value: aggregatedStats.p_bb, color: 'text-ink-2' },
                      { label: 'H',  value: aggregatedStats.p_h,  color: 'text-ink-2' },
                      { label: 'ER', value: aggregatedStats.p_er, color: 'text-ink-2' },
                      { label: '#P', value: aggregatedStats.totalPitches, color: 'text-ink-2' },
                    ].map(stat => (
                      <div key={stat.label} className="space-y-0.5 text-center">
                        <div className={cn("text-base font-black tracking-tight", stat.color)}>{stat.value}</div>
                        <div className="text-[8px] font-semibold text-ink-2 uppercase tracking-widest">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Behind the Plate — catcher development (passed balls / blocks) */}
              {((player?.seasonBlocks ?? 0) > 0 || (player?.seasonPassedBalls ?? 0) > 0) && (() => {
                const blocks = player.seasonBlocks ?? 0;
                const pb = player.seasonPassedBalls ?? 0;
                const chances = blocks + pb;
                const blockRate = chances > 0 ? Math.round((blocks / chances) * 100) : 0;
                return (
                  <div className="bg-canvas rounded-3xl border border-hairline p-5 space-y-4">
                    <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Behind the Plate</p>
                    <div className="grid grid-cols-3 gap-4">
                      {[
                        { label: 'Blocks', value: blocks, color: 'text-accent-ink' },
                        { label: 'Passed Balls', value: pb, color: pb > 0 ? 'text-danger' : 'text-ink-2' },
                        { label: 'Block%', value: chances > 0 ? `${blockRate}%` : '—', color: blockRate >= 80 ? 'text-accent-ink' : blockRate < 60 ? 'text-danger' : 'text-ink' },
                      ].map(stat => (
                        <div key={stat.label} className="space-y-0.5">
                          <div className={cn('text-lg font-black tracking-tight', stat.color)}>{stat.value}</div>
                          <div className="text-[8px] font-semibold text-ink-2 uppercase tracking-widest">{stat.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* xOVR Breakdown */}
              <XOVRBreakdownCard breakdown={player?.ovrBreakdown} ovr={player?.ovr} />

              {/* xOVR Trendline */}
              {player?.ovrBreakdown && (
                <XOVRTrendChart games={gamesForStats} player={player} />
              )}

              {/* Empty state — only if truly nothing to show. Guests never accrue
                  season stats, so they get a short, honest note instead of the
                  import walkthrough (importing won't ever give them stats). */}
              {(!aggregatedStats || (aggregatedStats.pa === 0 && aggregatedStats.p_gp === 0)) && (
                player?.isGuest ? (
                  <div className="bg-canvas rounded-3xl border border-hairline p-6 text-center space-y-2">
                    <p className="text-ink text-sm font-black uppercase tracking-widest">No season stats</p>
                    <p className="text-ink-2 text-[13px] font-medium leading-relaxed">
                      Guest players are for filling out a lineup on game day. Dugout doesn't
                      track their season stats. Add them as a full player to build a season.
                    </p>
                  </div>
                ) : (
                  <div className="bg-canvas rounded-3xl border border-hairline p-6 space-y-5">
                    <div className="text-center space-y-2">
                      <div className="w-11 h-11 rounded-2xl bg-ink flex items-center justify-center mx-auto">
                        <FileText className="w-5 h-5 text-surface" />
                      </div>
                      <p className="text-ink text-sm font-black uppercase tracking-widest">No stats yet</p>
                      <p className="text-ink-2 text-[13px] font-medium leading-relaxed">
                        {player?.name ? player.name.trim().split(' ')[0] : 'This player'}'s stats come
                        from your GameChanger scorebook. Import a game they played in and their
                        numbers show up here automatically.
                      </p>
                    </div>

                    {/* How to get stats in — three plain steps */}
                    <div className="space-y-3">
                      {[
                        { n: '1', t: 'Play a game and score it in GameChanger, like you already do.' },
                        { n: '2', t: 'Export the scorebook (CSV or PDF) from the GameChanger app.' },
                        { n: '3', t: 'Import it into Dugout from the Games tab. Stats fill in for everyone who played.' },
                      ].map(step => (
                        <div key={step.n} className="flex items-start gap-3">
                          <div className="w-5 h-5 rounded-full bg-ink flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-[10px] font-black text-surface">{step.n}</span>
                          </div>
                          <p className="text-ink-2 text-[13px] font-medium leading-relaxed">{step.t}</p>
                        </div>
                      ))}
                    </div>

                    {onGoToImport && (
                      <button
                        onClick={onGoToImport}
                        className="w-full flex items-center justify-center gap-2 bg-ink text-surface rounded-2xl py-3.5 text-[13px] font-black uppercase tracking-widest"
                      >
                        <Upload className="w-4 h-4" />
                        Go to Games to Import
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )
              )}
            </div>
          )}

          {/* ── Player Attributes tab ── */}
          {activeTab === 'attributes' && (
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2 space-y-2">
                  <label className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Player Name</label>
                  <input
                    required
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                    className="w-full bg-canvas border-hairline rounded-xl p-4 outline-none"
                    placeholder="Name"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">#</label>
                  <input
                    required
                    value={formData.number}
                    onChange={e => setFormData({...formData, number: e.target.value})}
                    className="w-full bg-canvas border-hairline rounded-xl p-4 outline-none"
                    placeholder="00"
                  />
                </div>
              </div>

              {/* Guest Player toggle */}
              <div className="flex items-center justify-between bg-canvas rounded-xl px-4 py-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest">Guest Player</p>
                  <p className="text-[10px] text-ink-2 mt-0.5">Stats excluded from team totals</p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({...formData, isGuest: !formData.isGuest})}
                  className={cn(
                    "px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest transition-all border",
                    formData.isGuest
                      ? "bg-gold-soft text-gold-ink border-hairline"
                      : "bg-raised text-ink-2 border-ink-mute"
                  )}
                >
                  {formData.isGuest ? 'Guest ✓' : 'Rostered'}
                </button>
              </div>
              {formData.isGuest && (
                <div className="flex items-center justify-between bg-canvas rounded-xl px-4 py-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest">Status</p>
                    <p className="text-[10px] text-ink-2 mt-0.5">Available for lineup &amp; live game</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, guestActive: !formData.guestActive})}
                    className={cn(
                      "px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest transition-all border",
                      formData.guestActive
                        ? "bg-canvas text-accent-ink border-ink-mute"
                        : "bg-raised text-ink-2 border-ink-mute"
                    )}
                  >
                    {formData.guestActive ? 'Active' : 'Inactive'}
                  </button>
                </div>
              )}

              {/* Attributes */}
              <div className="space-y-6">
                <h3 className="text-xs font-semibold text-accent-ink uppercase tracking-widest border-b border-hairline pb-2">Player Attributes (1-5)</h3>
                {Object.entries(formData.attributes).map(([category, attrs]: [string, any]) => {
                  if (category === 'pitching') {
                    return (
                      <div key="pitching" className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="text-[10px] font-bold text-ink-2 uppercase">Pitching</h4>
                          <button
                            type="button"
                            onClick={() => setIsPitcher(v => !v)}
                            className={cn(
                              "px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest transition-all border",
                              isPitcher
                                ? "bg-canvas text-accent-ink border-ink-mute"
                                : "bg-raised text-ink-2 border-ink-mute"
                            )}
                          >
                            {isPitcher ? 'Pitcher ✓' : 'Not a Pitcher'}
                          </button>
                        </div>
                        {isPitcher && (
                          <div className="grid gap-4">
                            {Object.entries(attrs).map(([key, val]: [string, any]) => (
                              <div key={key} className="flex items-center justify-between bg-canvas p-3 rounded-xl">
                                <span className="text-xs font-bold uppercase text-ink-2">{key}</span>
                                <div className="flex items-center space-x-2">
                                  {[1, 2, 3, 4, 5].map(num => (
                                    <button
                                      key={num}
                                      type="button"
                                      onClick={() => updateAttribute('pitching', key, num)}
                                      className={cn(
                                        "w-8 h-8 rounded-lg text-[10px] font-black transition-all",
                                        val === num ? "bg-ink text-surface scale-110" : "bg-raised text-ink-2"
                                      )}
                                    >
                                      {num}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                  return (
                    <div key={category} className="space-y-4">
                      <h4 className="text-[10px] font-bold text-ink-2 uppercase">{category}</h4>
                      <div className="grid gap-4">
                        {Object.entries(attrs).filter(([key]) => key !== 'block' || formData.primaryPosition === 'C').map(([key, val]: [string, any]) => (
                          <div key={key} className="flex items-center justify-between bg-canvas p-3 rounded-xl">
                            <span className="text-xs font-bold uppercase text-ink-2">{key}</span>
                            <div className="flex items-center space-x-2">
                              {[1, 2, 3, 4, 5].map(num => (
                                <button
                                  key={num}
                                  type="button"
                                  onClick={() => updateAttribute(category, key, num)}
                                  className={cn(
                                    "w-8 h-8 rounded-lg text-[10px] font-black transition-all",
                                    val === num ? "bg-ink text-surface scale-110" : "bg-raised text-ink-2"
                                  )}
                                >
                                  {num}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              <button
                type="submit"
                className="w-full py-4 bg-ink text-surface font-black rounded-2xl shadow-[0_0_30px_rgba(16,185,129,0.2)] uppercase italic"
              >
                {player ? 'Update Player' : 'Sign Player'}
              </button>

              {/* Roster management — existing players only */}
              {player?.id && (
                <div className="pt-2 space-y-3">
                  <div className="border-t border-hairline pt-4 space-y-1">
                    {/* Retire / Reactivate */}
                    {player.retired ? (
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await setDoc(doc(db, `teams/${teamId}/players`, player.id), { retired: false }, { merge: true });
                            onClose();
                          } catch (err) { console.error('Failed to reactivate player', err); }
                        }}
                        className="w-full py-3 rounded-2xl text-[12px] font-black uppercase tracking-widest border border-accent text-accent-ink bg-canvas"
                      >
                        Reactivate Player
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={async () => {
                          if (!window.confirm(`Retire ${player.name || 'this player'}? They'll move to the Retired section. Their stats and scouting history are kept, and you can reactivate them anytime.`)) return;
                          try {
                            await setDoc(doc(db, `teams/${teamId}/players`, player.id), { retired: true }, { merge: true });
                            onClose();
                          } catch (err) { console.error('Failed to retire player', err); }
                        }}
                        className="w-full py-3 rounded-2xl text-[12px] font-black uppercase tracking-widest border border-ink-mute text-ink-2 bg-canvas"
                      >
                        Retire Player
                      </button>
                    )}
                    <p className="text-[10px] text-ink-2 px-1 pt-1 leading-snug">
                      Moved away or no longer on the team? <span className="font-bold">Retire</span> keeps their season stats and hides them from the roster.
                    </p>
                  </div>

                  {/* Permanent delete — two-tap confirmation */}
                  <div>
                    <button
                      type="button"
                      onClick={async () => {
                        if (!confirmDelete) {
                          setConfirmDelete(true);
                          return;
                        }
                        try {
                          await deleteDoc(doc(db, `teams/${teamId}/players`, player.id));
                          onClose();
                        } catch (err) { console.error('Failed to delete player', err); }
                      }}
                      className={cn(
                        "w-full py-3 rounded-2xl text-[12px] font-black uppercase tracking-widest border",
                        confirmDelete
                          ? "border-[#DC2626] text-surface bg-[#DC2626]"
                          : "border-[#FCA5A5] text-[#DC2626] bg-surface"
                      )}
                    >
                      {confirmDelete ? 'Tap Again to Permanently Delete' : 'Delete Permanently'}
                    </button>
                    {confirmDelete && (
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(false)}
                        className="w-full py-2 mt-2 text-[11px] font-semibold uppercase tracking-widest text-ink-2"
                      >
                        Cancel
                      </button>
                    )}
                    <p className="text-[10px] text-ink-2 px-1 pt-1 leading-snug">
                      Deletes {player.name || 'this player'} and ALL of their stats. This can't be undone. To remove a player who left the team, use <span className="font-bold">Retire</span> instead.
                    </p>
                  </div>
                </div>
              )}
            </form>
          )}

          {/* ── Positions tab ── */}
          {activeTab === 'positions' && (
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="space-y-6">
                <h3 className="text-xs font-semibold text-accent-ink uppercase tracking-widest border-b border-hairline pb-2">Defensive Ranks (0-5)</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Primary Position</label>
                    <select
                      value={formData.primaryPosition}
                      onChange={e => setFormData({...formData, primaryPosition: e.target.value})}
                      className="w-full bg-canvas border-hairline rounded-xl p-3 text-xs font-bold outline-none"
                    >
                      <option value="">None</option>
                      {Object.keys(formData.positions).map(pos => (
                        <option key={pos} value={pos}>{pos}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Secondary Position</label>
                    <select
                      value={formData.secondaryPosition}
                      onChange={e => setFormData({...formData, secondaryPosition: e.target.value})}
                      className="w-full bg-canvas border-hairline rounded-xl p-3 text-xs font-bold outline-none"
                    >
                      <option value="">None</option>
                      {Object.keys(formData.positions).map(pos => (
                        <option key={pos} value={pos}>{pos}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {Object.entries(formData.positions).map(([pos, val]: [string, any]) => (
                    <div key={pos} className="bg-canvas p-3 rounded-xl space-y-2 text-center">
                      <span className="text-xs font-black text-ink-2">{pos}</span>
                      <select
                        value={val}
                        onChange={e => updatePosition(pos, parseInt(e.target.value))}
                        className="w-full bg-raised text-[10px] font-bold rounded-lg p-1 outline-none text-center"
                      >
                        {[0, 1, 2, 3, 4, 5].map(n => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                className="w-full py-4 bg-ink text-surface font-black rounded-2xl shadow-[0_0_30px_rgba(16,185,129,0.2)] uppercase italic"
              >
                {player ? 'Update Player' : 'Sign Player'}
              </button>
            </form>
          )}

        </div>
      </motion.div>

      {reportOpen && fieldTimeReport && (
        <FieldTimeReportModal data={fieldTimeReport} onClose={() => setReportOpen(false)} />
      )}
    </motion.div>
  );
};
